import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User, Tenant } from '@/types/auth';
import { disconnectSocket } from '@/lib/socket';

interface SuperAdminSession {
  user: User;
  tenant: Tenant;
  accessToken: string;
  refreshToken: string;
}

interface AuthState {
  user: User | null;
  tenant: Tenant | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isSubscriptionSuspended: boolean;
  isImpersonating: boolean;
  impersonationReadOnly: boolean;
  superAdminSession: SuperAdminSession | null;

  setAuth: (user: User, tenant: Tenant, accessToken: string, refreshToken: string) => void;
  setTokens: (accessToken: string, refreshToken: string) => void;
  setUser: (user: User) => void;
  setTenant: (tenant: Tenant) => void;
  setSubscriptionSuspended: (suspended: boolean) => void;
  logout: () => void;
  hasPermission: (permission: string) => boolean;
  hasRole: (role: string) => boolean;
  refreshPermissions: () => Promise<void>;
  startImpersonation: (user: User, tenant: Tenant, accessToken: string, refreshToken: string) => void;
  stopImpersonation: () => void;
  setImpersonationReadOnly: (readOnly: boolean) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      tenant: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      isSubscriptionSuspended: false,
      isImpersonating: false,
      impersonationReadOnly: false,
      superAdminSession: null,

      setAuth: (user, tenant, accessToken, refreshToken) => {
        set({
          user,
          tenant,
          accessToken,
          refreshToken,
          isAuthenticated: true,
          isSubscriptionSuspended: tenant.subscriptionEnabled === false,
        });
        // POS-I226: the account's appearance overrides the local cache (lazy import: no circular dependency)
        import('@/stores/appearance.store').then(({ useAppearanceStore }) => {
          useAppearanceStore.getState().applyFromServer((user as { uiPreferences?: unknown } | null)?.uiPreferences);
        }).catch(() => {});
        // Sync language preference from logged-in user
        const preferredLanguage = (user as { preferredLanguage?: 'en' | 'ur' | 'ro' } | null)?.preferredLanguage;
        if (preferredLanguage) {
          import('@/stores/i18n.store').then(({ useI18nStore }) => {
            useI18nStore.getState().setLanguage(preferredLanguage);
          }).catch(() => {});
        }
      },

      setTokens: (accessToken, refreshToken) => {
        set({ accessToken, refreshToken });
      },

      setUser: (user) => {
        set({ user });
      },

      setTenant: (tenant) => {
        set({ tenant, isSubscriptionSuspended: tenant.subscriptionEnabled === false });
      },

      setSubscriptionSuspended: (suspended) => {
        set({ isSubscriptionSuspended: suspended });
      },

      logout: () => {
        disconnectSocket();
        set({
          user: null,
          tenant: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
          isSubscriptionSuspended: false,
          isImpersonating: false,
          superAdminSession: null,
        });
      },

      hasPermission: (permission: string) => {
        const user = get().user;
        if (!user) return false;
        if (user.isSuperAdmin) return true;
        const perms = user.permissions ?? [];
        return perms.some(
          (p) => p === permission || p === '*' || (p.endsWith(':*') && permission.startsWith(p.replace(':*', ':'))),
        );
      },

      hasRole: (role: string) => {
        const user = get().user;
        if (!user) return false;
        return (user.roles ?? []).includes(role);
      },

      refreshPermissions: async () => {
        const { accessToken, isAuthenticated } = get();
        if (!isAuthenticated || !accessToken) return;

        try {
          const res = await fetch('/api/v1/auth/me', {
            headers: { Authorization: `Bearer ${accessToken}` },
          });
          if (!res.ok) return;

          const data = await res.json();
          const { user: freshUser, tenant: freshTenant } = data;

          // POS-I109: drop a late answer — the user signed out or someone else signed in
          // while /auth/me was in flight, so it must not be merged into the new session.
          if (get().accessToken !== accessToken) return;
          if (freshUser?.id && get().user?.id && freshUser.id !== get().user?.id) return;

          set((state) => ({
            user: state.user ? { ...state.user, ...freshUser } : freshUser,
            tenant: freshTenant
              ? { ...state.tenant, ...freshTenant }
              : state.tenant,
            isSubscriptionSuspended: freshTenant?.subscriptionEnabled === false,
          }));

          import('@/stores/appearance.store').then(({ useAppearanceStore }) => {
            if (freshUser && 'uiPreferences' in freshUser) useAppearanceStore.getState().applyFromServer(freshUser.uiPreferences);
          }).catch(() => {});

          // Sync language preference from server (lazy import to avoid circular dep)
          if (freshUser?.preferredLanguage) {
            import('@/stores/i18n.store').then(({ useI18nStore }) => {
              const { language, setLanguage } = useI18nStore.getState();
              if (freshUser.preferredLanguage !== language) {
                setLanguage(freshUser.preferredLanguage);
              }
            }).catch(() => {});
          }
        } catch {
          // Network error — ignore, will retry next interval
        }
      },

      startImpersonation: (user, tenant, accessToken, refreshToken) => {
        const state = get();
        set({
          superAdminSession: {
            user: state.user!,
            tenant: state.tenant!,
            accessToken: state.accessToken!,
            refreshToken: state.refreshToken!,
          },
          user,
          tenant,
          accessToken,
          refreshToken,
          isAuthenticated: true,
          isImpersonating: true,
          impersonationReadOnly: true,
          isSubscriptionSuspended: tenant.subscriptionEnabled === false,
        });
      },

      stopImpersonation: () => {
        const { superAdminSession } = get();
        if (!superAdminSession) return;
        set({
          user: superAdminSession.user,
          tenant: superAdminSession.tenant,
          accessToken: superAdminSession.accessToken,
          refreshToken: superAdminSession.refreshToken,
          isAuthenticated: true,
          isImpersonating: false,
          impersonationReadOnly: false,
          superAdminSession: null,
          isSubscriptionSuspended: false,
        });
      },

      setImpersonationReadOnly: (readOnly) => {
        set({ impersonationReadOnly: readOnly });
      },
    }),
    {
      name: 'clearring-auth',
      partialize: (state) => ({
        user: state.user,
        tenant: state.tenant,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
        isSubscriptionSuspended: state.isSubscriptionSuspended,
        isImpersonating: state.isImpersonating,
        impersonationReadOnly: state.impersonationReadOnly,
        superAdminSession: state.superAdminSession,
      }),
    },
  ),
);
